import { useState, useRef } from "react";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import FilterPill from "./filter/FilterPill";
import WorkspacePill from "./filter/WorkspacePill";
import Conversation from "@/models/conversation/Conversation";
import SourceSelectionDialog from "./dialogs/SourceSelectionDialog";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Scale, BriefcaseBusiness } from "lucide-react";
import { cn } from "@/lib/utils";
import Workspace from "@/models/workspace/Workspace";
import { WorkspaceSelectionDialog } from "../blocks/workspace-selection-dialog";
import { WorkspaceContextCapBanner } from "../blocks/WorkspaceContextCapBanner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { ScrollArea } from "@capacit-com/lara-law-shared-ui";
import { useContent } from "@/hooks/useContent";

import { usePillMeasurement } from "../hooks/usePillMeasurement";
import { useWorkspaceDialogSync } from "../hooks/useWorkspaceDialogSync";
import useLegislationFilters from "@/hooks/useLegislationFilters";

type ContextItem = {
  type: "filter" | "workspace";
  key: string;
  source?: string;
  filterIndex?: number;
  filters?: Record<string, unknown>;
  workspaceId?: string;
  selectedIds?: string[];
};

type Props = {
  selectedConversation: Conversation | undefined;
  workspaces?: Workspace[];
  isLoadingWorkspaces?: boolean;
};

const SelectedContext = ({
  selectedConversation,
  workspaces = [],
  isLoadingWorkspaces = false,
}: Props) => {
  const { t } = useContent();
  const { filters, resetFilters, workspaceContext } =
    useNewConversationFiltersStore();
  const { useGetFilterConfiguration } = useLegislationFilters();
  const fetchedFilterConfiguration = useGetFilterConfiguration();
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const [overflowPopoverOpen, setOverflowPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pillsContainerRef = useRef<HTMLDivElement>(null);

  const numberOfSources = Object.values(filters).flat().length;
  const numberOfWorkspaces = Object.values(workspaceContext).length;
  const hasContext = numberOfSources > 0 || numberOfWorkspaces > 0;
  const canEdit = !selectedConversation;

  const hasWorkspaceContext = numberOfWorkspaces > 0;
  const hasLegalSources = numberOfSources > 0;
  const canAddSources = !hasWorkspaceContext;
  const canAddWorkspace = !hasLegalSources;

  // Build all context items
  const allContextItems: ContextItem[] = [];
  Object.entries(filters).forEach(([source, sourceFilterArray]) => {
    sourceFilterArray.forEach((filterGroup, idx) => {
      allContextItems.push({
        type: "filter",
        key: `filter-${source}-${idx}`,
        source,
        filterIndex: idx,
        filters: filterGroup,
      });
    });
  });
  Object.entries(workspaceContext).forEach(([workspaceId, selectedIds]) => {
    allContextItems.push({
      type: "workspace",
      key: `workspace-${workspaceId}`,
      workspaceId,
      selectedIds: selectedIds.map((doc) => doc.documentId),
    });
  });

  const totalItems = allContextItems.length;

  // Use measurement hook
  const { visibleCount, recalculate } = usePillMeasurement({
    containerRef,
    pillsContainerRef,
    totalItems,
    hasContext,
  });

  // Use workspace dialog sync hook
  const {
    selectedItems,
    handleSelectItem,
    handleDeselectWorkspace,
    handleSelectAllWorkspaces,
    commitSelection,
    resetSelection,
  } = useWorkspaceDialogSync({
    workspaceDialogOpen,
    workspaces,
  });

  const overflowItems = allContextItems.slice(visibleCount);
  const hasOverflow = overflowItems.length > 0 && visibleCount < totalItems;

  // Helper function to normalize keys for comparison (matches SearchOption.ts)
  // Handles Danish characters: æ→ae, ø→o, å→a
  const normalizeKey = (s: string) =>
    s
      .toLowerCase()
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "a")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // remove diacritics
      .replace(/\s+/g, "")
      .replace(/-/g, "");

  // Helper function to get displayName from sourceName
  // Handles both display name keys and backend enum values
  const getSourceDisplayName = (sourceName: string): string => {
    if (
      fetchedFilterConfiguration.isFetched &&
      fetchedFilterConfiguration.data
    ) {
      // First try direct lookup (for display name keys)
      const sourceDef = fetchedFilterConfiguration.data.sources[sourceName];
      if (sourceDef?.displayName) {
        return sourceDef.displayName;
      }

      // If not found, try reverse lookup by normalizing backend enum values
      // Normalize the input sourceName
      const normalizedInput = normalizeKey(sourceName);

      // Iterate through all sources to find a match
      for (const [configKey, configSource] of Object.entries(
        fetchedFilterConfiguration.data.sources
      )) {
        const normalizedConfigKey = normalizeKey(configKey);
        if (normalizedConfigKey === normalizedInput) {
          return configSource.displayName;
        }
      }
    }
    return sourceName;
  };

  const renderPill = (
    item: ContextItem,
    options?: {
      inPopover?: boolean;
      onRemoveCallback?: () => void;
    }
  ) => {
    if (
      item.type === "filter" &&
      item.source &&
      item.filterIndex !== undefined
    ) {
      return (
        <FilterPill
          key={item.key}
          pillIndex={item.filterIndex}
          source={item.source}
          displayName={getSourceDisplayName(item.source!)}
          filters={item.filters || {}}
          onRemoveSource={() => {
            // removeFilter(item.source!, item.filterIndex!);
            resetFilters();
            options?.onRemoveCallback?.();
          }}
          inPopover={options?.inPopover}
          isOverview={!!selectedConversation}
          dataPillItem
        />
      );
    } else if (item.type === "workspace" && item.workspaceId) {
      return (
        <WorkspacePill
          key={item.key}
          workspaceId={item.workspaceId}
          selectedIds={item.selectedIds || []}
          inPopover={options?.inPopover}
          isOverview={!!selectedConversation}
          dataPillItem
          dataWorkspacePill
        />
      );
    }
    return null;
  };

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          "w-full flex flex-wrap items-center gap-2 px-1.5 border-b border-border/40 min-h-[40px]",
          !hasContext && "justify-center"
        )}
      >
        {hasContext ? (
          <div
            ref={pillsContainerRef}
            data-pills-container
            className="flex items-center gap-2 flex-1 min-w-0"
            style={{
              maxWidth: "calc(100% - 180px)",
              overflow: "hidden",
              flexWrap: "nowrap",
              position: "relative",
            }}
          >
            {/* Visible pills */}
            {allContextItems.slice(0, visibleCount).map((item) => (
              <div key={item.key}>{renderPill(item)}</div>
            ))}
            {/* Hidden container with all pills for measurement */}
            <div
              data-measurement-container
              style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                visibility: "hidden",
                display: "flex",
                gap: "8px",
                flexWrap: "nowrap",
              }}
              aria-hidden="true"
            >
              {allContextItems.map((item) => (
                <div key={`measure-${item.key}`}>{renderPill(item)}</div>
              ))}
            </div>
            {hasOverflow && (
              <Popover
                open={overflowPopoverOpen}
                onOpenChange={setOverflowPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Badge
                    variant="outline"
                    className="flex items-center space-x-1 px-2 py-1 border-primary cursor-pointer hover:bg-accent"
                    onMouseEnter={() => setOverflowPopoverOpen(true)}
                    data-pill-item
                  >
                    <span className="font-medium">+{overflowItems.length}</span>
                  </Badge>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 p-0"
                  onMouseEnter={() => setOverflowPopoverOpen(true)}
                  onMouseLeave={() => setOverflowPopoverOpen(false)}
                  align="start"
                >
                  <ScrollArea className="max-h-96">
                    <div className="p-2 space-y-2">
                      {overflowItems.map((item) => (
                        <div key={item.key}>
                          {renderPill(item, {
                            inPopover: true,
                            onRemoveCallback: () => {
                              setTimeout(() => recalculate(), 100);
                            },
                          })}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ) : canEdit ? (
          <span className="text-xs text-muted-foreground">
            {t("chat.context.noContextSelected")}
          </span>
        ) : (
          <span className="text-xs text-destructive italic">
            {t("chat.context.noContextWarning")}
          </span>
        )}
        {canEdit && (
          <div className="flex gap-1.5 shrink-0" data-buttons-container>
            {canAddSources ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSourceDialogOpen(true)}
                className="h-7 px-2 text-xs"
              >
                <Scale className="size-3 mr-1.5" />
                {t("chat.context.addSource")}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      className="h-7 px-2 text-xs"
                    >
                      <Scale className="size-3 mr-1.5" />
                      {t("chat.context.addSource")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[250px]">
                    {t("chat.context.cannotAddSourceMessage")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {canAddWorkspace ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWorkspaceDialogOpen(true)}
                className="h-7 px-2 text-xs"
              >
                <BriefcaseBusiness className="size-3 mr-1.5" />
                {t("chat.context.addWorkspace")}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      className="h-7 px-2 text-xs"
                    >
                      <BriefcaseBusiness className="size-3 mr-1.5" />
                      {t("chat.context.addWorkspace")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[250px]">
                    {t("chat.context.cannotAddWorkspaceMessage")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {hasLegalSources && (
        <div className="w-full px-1.5 pt-1 pb-1 border-b border-border/40">
          <span className="text-xs text-muted-foreground">
            {t("chat.context.currentDataOnly")}
          </span>
        </div>
      )}

      <SourceSelectionDialog
        open={sourceDialogOpen}
        onOpenChange={setSourceDialogOpen}
      />

      <WorkspaceSelectionDialog
        open={workspaceDialogOpen}
        onOpenChange={(open) => {
          if (!open) resetSelection();
          setWorkspaceDialogOpen(open);
        }}
        title={t("chat.context.selectWorkspaceTitle")}
        workspaces={workspaces}
        isLoading={isLoadingWorkspaces}
        emptyMessage={t("chat.context.noWorkspaceFilesFound")}
        selectedItems={selectedItems.map((s) => s.item)}
        onSelectItem={handleSelectItem}
        onDeselectWorkspace={handleDeselectWorkspace}
        onSelectAllWorkspaces={handleSelectAllWorkspaces}
        onConfirm={() => {
          commitSelection();
          setWorkspaceDialogOpen(false);
        }}
        confirmLabel={t("chat.context.confirm")}
        showSearch
        explorerDisabled={hasLegalSources}
        warningBanner={
          <div className="flex shrink-0 flex-col gap-2">
            {hasLegalSources ? (
              <div className="p-3 bg-warning/10 border border-warning/30 rounded-md">
                <p className="text-sm text-foreground">
                  {t("chat.context.alreadyHasSourcesMessage")}
                </p>
              </div>
            ) : null}
            <WorkspaceContextCapBanner selectedItems={selectedItems} />
          </div>
        }
      />
    </>
  );
};

export default SelectedContext;
